/**
 * KPI / dashboard domain API functions (Step 7.1.3).
 *
 * Occupancy, profit, MRR, dashboard KPIs, alerts, critical units,
 * revenue forecast, invoice summary, co-living metrics, profit ranking.
 * Extracted from adminData/index.js for team scalability.
 */
import { API_BASE_URL, getApiHeaders } from "./core";

export function fetchAdminOccupancy(params = {}) {
  const sp = new URLSearchParams();
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  if (params.on_date) sp.set("on_date", params.on_date);
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/occupancy${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Occupancy konnte nicht geladen werden.");
    return res.json();
  });
}

/**
 * Fetch per-room occupancy for a unit (for OccupancyMap). Params: unit_id (required), on_date? (YYYY-MM-DD).
 */
export function fetchAdminOccupancyRooms(params = {}) {
  const sp = new URLSearchParams();
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  if (params.on_date) sp.set("on_date", params.on_date);
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/occupancy/rooms?${qs}`;
  return fetch(url, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Room occupancy konnte nicht geladen werden.");
    return res.json();
  });
}

/**
 * Fetch revenue forecast. Params: year, month? (1-12; if omitted returns full year).
 */
export function fetchAdminRevenueForecast(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/revenue-forecast?${qs}`;
  return fetch(url, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Revenue Forecast konnte nicht geladen werden.");
    return res.json();
  });
}

/**
 * Fetch org-level total recurring revenue for one month
 * (GET /api/admin/revenue/monthly-forecast?year=YYYY&month=MM).
 * Returns camelCase mapping of the backend response. Throws on non-2xx.
 */
export function fetchAdminMonthlyForecast(year, month) {
  const sp = new URLSearchParams();
  if (year != null) sp.set("year", String(year));
  if (month != null) sp.set("month", String(month));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/revenue/monthly-forecast${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Monats-Forecast konnte nicht geladen werden.");
      return res.json();
    })
    .then((data) => ({
      year: data.year,
      month: data.month,
      organizationId: data.organization_id,
      totalForecastChf: Number(data.total_forecast_chf ?? 0),
      activeTenancyCount: Number(data.active_tenancy_count ?? 0),
      tenanciesWithRentRevenueCount: Number(data.tenancies_with_rent_revenue_count ?? 0),
      tenanciesWithoutRentRevenueCount: Number(data.tenancies_without_rent_revenue_count ?? 0),
      currency: data.currency || "CHF",
    }));
}

/**
 * Fetch invoice summary KPIs: open_invoices_count, paid_invoices_count,
 * overdue_invoices_count, open_invoices_amount.
 */
export function fetchAdminInvoiceSummary() {
  return fetch(`${API_BASE_URL}/api/admin/invoice-summary`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) throw new Error("Invoice summary konnte nicht geladen werden.");
    return res.json();
  });
}

/**
 * Fetch Co-Living metrics for one unit in a given month (Step 4.3).
 * Returns: revenue, full_potential, vacancy_loss, occupied/reserved/free counts, total_rooms, occupancy_rate.
 * @param {string} unitId
 * @param {string} [month] YYYY-MM (default: current month)
 */
export function fetchAdminCoLivingMetrics(unitId, month) {
  const sp = new URLSearchParams();
  if (month) sp.set("month", month);
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/units/${encodeURIComponent(unitId)}/co-living-metrics${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Co-Living Metriken konnten nicht geladen werden.");
    return res.json();
  });
}

/**
 * Fetch profit per unit (revenue - costs) for a given month.
 * Revenue/costs match backend `revenue_forecast` + `profit_service` for the period.
 * Params: year?, month? (default current), unit_id? (optional filter).
 */
export function fetchAdminProfit(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/profit${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Profit konnte nicht geladen werden.");
    return res.json();
  });
}

/**
 * Strip browser/extension noise (e.g. postMessage) from user-facing error text.
 * Logs the raw message to the console; callers should show the returned string only.
 */
export function sanitizeClientErrorMessage(message, fallback) {
  const raw = String(message || "");
  if (/postmessage/i.test(raw) || /target origin/i.test(raw)) {
    console.warn("Ignored non-actionable error:", raw);
    return fallback || "Ein unerwarteter Fehler ist aufgetreten.";
  }
  return raw || fallback || "";
}

function sanitizeNonActionableUiMessage(msg) {
  const raw = String(msg || "");
  if (/postmessage/i.test(raw) || /target origin/i.test(raw)) {
    console.warn("Ignored non-actionable error:", raw);
    return "";
  }
  return raw;
}

/**
 * Normalize fetch failure for dashboard: "Failed to fetch" means network/CORS/server unreachable.
 * Exported so overview page can use the same message for operations load errors.
 */
export function normalizeFetchError(e, fallbackMessage) {
  const msg = e?.message || "";
  if (msg === "Failed to fetch" || msg.includes("Load failed") || msg.includes("NetworkError")) {
    return new Error(
      "Verbindung zum Server fehlgeschlagen. Bitte Backend-URL (REACT_APP_API_URL) und CORS prüfen."
    );
  }
  const base = e instanceof Error ? e : new Error(fallbackMessage);
  const cleaned = sanitizeNonActionableUiMessage(base.message);
  if (!cleaned) {
    return new Error(fallbackMessage);
  }
  return new Error(cleaned);
}

/**
 * Dashboard KPIs: avg revenue per room, avg profit per unit, weakest/best unit,
 * vacant days, break-even, forecast, trend, warnings. Params: year?, month? (default current).
 * 6A.1.1 range mode: range_mode="all" ("Gesamte Zeit") aggregates the two money KPIs
 * over ALL invoices (no period bound). The from/to params remain for the 6A.3 custom path.
 */
export function fetchAdminDashboardKpis(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.range_mode != null) sp.set("range_mode", String(params.range_mode));
  if (params.from_year != null) sp.set("from_year", String(params.from_year));
  if (params.from_month != null) sp.set("from_month", String(params.from_month));
  if (params.to_year != null) sp.set("to_year", String(params.to_year));
  if (params.to_month != null) sp.set("to_month", String(params.to_month));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/dashboard/kpis${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("KPI-Daten konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "KPI-Daten konnten nicht geladen werden.");
    });
}

/**
 * KPI A.2 — Monatlicher Mietwert (MRR). Params: on_date (YYYY-MM-DD), Stichtag.
 */
export function fetchAdminMrr(params = {}) {
  const sp = new URLSearchParams();
  if (params.on_date) sp.set("on_date", String(params.on_date));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/mrr${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("MRR konnte nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "MRR konnte nicht geladen werden.");
    });
}

/**
 * Barkautionen (Stichtag) — total cash deposits currently held. No params.
 */
export function fetchAdminCashDepositsHeld() {
  const url = `${API_BASE_URL}/api/admin/deposits/cash-held`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Barkautionen konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Barkautionen konnten nicht geladen werden.");
    });
}

/**
 * Barkautionen (Stichtag) — Detailliste: aggregate (total/count/as_of_date)
 * plus one item per held cash deposit. Self-contained for the detail modal.
 */
export function fetchAdminCashDepositsHeldDetails() {
  const url = `${API_BASE_URL}/api/admin/deposits/cash-held/details`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok)
        throw new Error("Barkautionen-Details konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(
        e,
        "Barkautionen-Details konnten nicht geladen werden."
      );
    });
}

/** KPI C.6 — Kritische Einheiten (Stichtag immer heute im Backend). */
export function fetchAdminCriticalUnits() {
  return fetch(`${API_BASE_URL}/api/admin/critical-units`, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Kritische Einheiten konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Kritische Einheiten konnten nicht geladen werden.");
    });
}

/** KPI_SEMANTICS v2.1.1 Kapitel D — Medium-Severity Alerts (GET /api/admin/alerts). */
export function fetchAdminAlerts() {
  return fetch(`${API_BASE_URL}/api/admin/alerts`, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Alerts konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Alerts konnten nicht geladen werden.");
    });
}

/** KPI C.5 — Ranking nach Profit-Marge. Params: year, month (required). */
export function fetchAdminProfitRankedByMargin(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/profit/ranked-by-margin${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Profit-Ranking konnte nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Profit-Ranking konnte nicht geladen werden.");
    });
}

/**
 * GET /api/admin/dashboard/cost-breakdown — Vantio operating costs for one
 * calendar month, by category (A2a/A2b). Read-only.
 *
 * `operating_costs.total_chf` is the sum of FIVE buckets: rent, utilities,
 * internet, cleaning and `deposit_insurance_chf`. The premium is ALREADY inside
 * the total — adding it again on the client double-counts it.
 *
 * Unrecognised `cost_type` values never reach the total. They are reported under
 * `unclassified` with their own monthly figure, which must not be folded into an
 * operating-cost headline or a contribution margin.
 *
 * Deliberately NOT `/dashboard/kpis`: that endpoint's `summary.total_costs` sums
 * every cost row without classifying it, so production's free-text "Kaution" row
 * is counted there as an operating expense.
 *
 * `to_year`/`to_month` (P3a) extend the selection to an inclusive month WINDOW. The
 * window is folded SERVER-SIDE from the same per-month truth — never a month
 * multiplied by a count — and the response's `semantics.range_aggregation` says so.
 * Both or neither: a half-given window is a 422, and completing it here would
 * answer a period nobody asked for. Omitted, the request is unchanged.
 *
 * What a window does NOT buy: `unit_costs` carries no `valid_from`/`valid_to`, so a
 * row edited today applies to every month of the window. That is the limitation the
 * single-month contract always had, spread over more months, and the response states
 * it in `semantics.historical_cost_rows_versioned`.
 */
export function fetchDashboardCostBreakdown(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.to_year != null && params.to_month != null) {
    sp.set("to_year", String(params.to_year));
    sp.set("to_month", String(params.to_month));
  }
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/dashboard/cost-breakdown${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders(), signal: params.signal })
    .then((res) => {
      if (!res.ok) throw new Error("Betriebskosten konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Betriebskosten konnten nicht geladen werden.");
    });
}

/**
 * GET /api/admin/dashboard/cost-ranking — units ranked by operating cost for one
 * calendar month (A2a). Read-only.
 *
 * THE SAME FIVE BUCKETS as `fetchDashboardCostBreakdown`, per unit. The order is
 * the contract's own: `operating_cost_total_chf` descending, ties resolved on
 * `short_unit_id` then `unit_id`. Never re-sort the rows on the client — the
 * backend orders on the OPERATING total, and sorting on anything the response
 * also carries (the raw row sum, `unclassified_total_chf`) would put a unit at
 * the top of a list that claims to rank operating spend.
 *
 * NOT `revenue-analytics.top_units`. That list exists, carries the same name and
 * ranks by `expected_recurring_rent` — revenue, not cost. Two different
 * questions; reading the wrong one produces a plausible list of the wrong units.
 *
 * `units_ranked` is the count BEFORE `limit`, so a caller can tell "these are all
 * of them" from "this is the top 5 of forty".
 *
 * A unit with CHF 0 operating cost is a real result and is returned; a unit whose
 * lease does not overlap the period is absent. Neither is a client decision.
 *
 * `to_year`/`to_month` (P3a) extend the selection to an inclusive month WINDOW,
 * and the ORDER is recomputed over that window server-side — over six months a
 * unit can outspend one that led the last month of them. Both or neither: a
 * half-given window is a 422, and completing it here would rank a period nobody
 * asked for. Omitted, the request is the single-month one it has always been.
 */
export function fetchDashboardCostRanking(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.to_year != null && params.to_month != null) {
    sp.set("to_year", String(params.to_year));
    sp.set("to_month", String(params.to_month));
  }
  if (params.limit != null) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/dashboard/cost-ranking${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders(), signal: params.signal })
    .then((res) => {
      if (!res.ok) throw new Error("Kostenranking konnte nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Kostenranking konnte nicht geladen werden.");
    });
}

/**
 * GET /api/admin/occupancy/average — average daily portfolio occupancy across an
 * inclusive date range (KPI C.2).
 *
 * The month-accurate counterpart to `fetchAdminOccupancy`, which answers for a
 * single day. A point-in-time rate shown beside "August 2026" would read as the
 * month's rate while actually describing one date, so a month card uses this and
 * passes the month's first and last day.
 */
export function fetchAdminOccupancyAverage(params = {}) {
  const sp = new URLSearchParams();
  if (params.start_date) sp.set("start_date", params.start_date);
  if (params.end_date) sp.set("end_date", params.end_date);
  const url = `${API_BASE_URL}/api/admin/occupancy/average?${sp.toString()}`;
  return fetch(url, { headers: getApiHeaders(), signal: params.signal })
    .then((res) => {
      if (!res.ok) throw new Error("Auslastung konnte nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Auslastung konnte nicht geladen werden.");
    });
}

/**
 * GET /api/admin/occupancy/room-days — room-day occupancy over an inclusive month
 * window (KPI **C.4**). Read-only.
 *
 * `Σ occupied_room_days / Σ available_room_days`. This is the MANAGEMENT occupancy:
 * the figure a management view shows when a PERIOD is selected, and the same
 * room-day family the Performance tab divides.
 *
 * THREE OCCUPANCY CONTRACTS, AND THEY ARE NOT INTERCHANGEABLE:
 *
 *   `/occupancy`          C.1 — one DAY, the point-in-time snapshot.
 *   `/occupancy/average`  C.2 — the UNWEIGHTED MEAN of the daily rates in a span.
 *   `/occupancy/room-days` C.4 — the RATIO OF SUMS over a month window.
 *
 * C.2 and C.4 answer differently whenever the months are unequal in length or the
 * portfolio changed size inside the window, and both are correct for the question
 * they were asked. February at 100 % and August at 0 % is 47.5 % here and 50 % there.
 * So this is a separate helper rather than a flag on `fetchAdminOccupancyAverage`:
 * one function with a switch is how a caller picks the wrong KPI by accident.
 *
 * `from_year`/`from_month` are REQUIRED — this endpoint has no "current month"
 * default, because it exists to answer for a period the caller names. `to_*` default
 * to the from-month server-side, so a single month is the degenerate window and not a
 * second code path.
 *
 * The counters are the truth and `occupancy_pct` is a view of them; it is already a
 * percentage and is never scaled again on the client.
 *
 * @param {{ from_year: number, from_month: number, to_year?: number, to_month?: number, signal?: AbortSignal }} params
 * @returns {Promise<object>} OccupancyRoomDaysResponse
 */
export function fetchAdminOccupancyRoomDays(params = {}) {
  const sp = new URLSearchParams();
  if (params.from_year != null) sp.set("from_year", String(params.from_year));
  if (params.from_month != null) sp.set("from_month", String(params.from_month));
  if (params.to_year != null && params.to_month != null) {
    sp.set("to_year", String(params.to_year));
    sp.set("to_month", String(params.to_month));
  }
  const url = `${API_BASE_URL}/api/admin/occupancy/room-days?${sp.toString()}`;
  return fetch(url, { headers: getApiHeaders(), signal: params.signal })
    .then((res) => {
      if (!res.ok) throw new Error("Auslastung konnte nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Auslastung konnte nicht geladen werden.");
    });
}

/**
 * GET /api/admin/deposits/landlord-placed — capital currently placed WITH
 * landlords (A2b). Point in time, org-scoped, no parameters.
 *
 * NO PERIOD IS ACCEPTED AND NONE MAY BE ADDED. The unit carries no
 * `deposited_at` / `released_at` columns — an edit overwrites the only value
 * that exists — so the endpoint answers "now" and stamps its own `as_of`. The
 * response says as much in `semantics.historical_snapshot_available`.
 *
 * NOT `fetchAdminCashDepositsHeld`. That one answers with `cash_deposits_total`
 * — money TENANTS placed with us, a liability. This one answers with `total_chf`
 * — capital WE placed with an owner. Two sides of the balance sharing one German
 * word; the two figures are never summed.
 *
 * The `annual_premium_total_chf` it carries is an ANNUAL guarantee cost and is
 * context only: its monthly, lease-prorated form is already counted as an
 * operating cost by `/api/admin/dashboard/cost-breakdown` under
 * `deposit_insurance_chf`.
 */
export function fetchAdminLandlordPlacedDeposits() {
  const url = `${API_BASE_URL}/api/admin/deposits/landlord-placed`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok)
        throw new Error("Sicherheitsleistungen konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Sicherheitsleistungen konnten nicht geladen werden.");
    });
}

/**
 * GET /api/admin/deposits/tenant-held — securities TENANTS have placed with us
 * (K2). Point in time, org-scoped, no parameters.
 *
 * THE MIRROR OF `fetchAdminLandlordPlacedDeposits`, NOT A VARIANT OF IT. That one
 * answers with capital WE placed with an owner — an asset. This one answers with
 * money and guarantees tenants placed with US — a liability, third-party money.
 * The two `total_chf` figures are never summed.
 *
 * NOT `fetchAdminCashDepositsHeld` either. That endpoint answers the narrower
 * question "how much CASH is held", and its `cash_deposits_total` equals this
 * response's `by_type.cash.total_chf` exactly. This one additionally carries bank
 * deposits and insurance guarantees.
 *
 * PART OF `total_chf` IS A GUARANTEE, NOT A BALANCE. An insurance deposit is a sum
 * an insurer would pay, money that never moved; `semantics.insurance_amount_is_
 * guarantee` says so on the wire. Presenting the total as liquidity would repeat,
 * on the tenant side, the error K1 corrected on the landlord side.
 *
 * NO PERIOD IS ACCEPTED AND NONE MAY BE ADDED. `tenancies` records when a deposit
 * arrived or went back but keeps no validity history, so the endpoint answers
 * "now" and stamps its own `as_of_date` from the Swiss business day.
 *
 * The `annual_premium_total_chf` it carries is the TENANT's own insurance premium
 * — context, not our operating cost. Our premium is the landlord response's field
 * of the same name.
 */
export function fetchAdminTenantHeldDeposits() {
  const url = `${API_BASE_URL}/api/admin/deposits/tenant-held`;
  return fetch(url, { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Mieterkautionen konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Mieterkautionen konnten nicht geladen werden.");
    });
}

/**
 * GET /api/admin/dashboard/performance — the Performance 2.0 contract (S4).
 *
 * ONE month of expected recurring revenue, classified operating costs, the
 * result and margin they imply, room-day occupancy, and the R2 ranking over the
 * units they are summed from. The previous month is derived server-side and
 * comes back in the same response, so a month-over-month comparison costs no
 * second request.
 *
 * Deliberately NOT `/dashboard/profit` or `/dashboard/kpis`. Those fold every
 * cost row into one total without classifying it — in production that total
 * carries a free-text "Kaution" row as an operating expense — and they call the
 * remainder a profit, which it is not: personnel, overheads, marketing,
 * accounting, financing, taxes and depreciation reach no cost truth in this
 * repository. This endpoint classifies fail-closed, keeps the unclassified
 * remainder visible and separate, and publishes its own methodology.
 *
 * MONEY ARRIVES AS A STRING. Pydantic serializes Decimal losslessly as JSON
 * text ("1500.55", and "2400.0" for a whole-franc sum — the SCALE is whatever
 * the arithmetic produced, not a fixed two places). Callers must parse rather
 * than pattern-match, and must never test `typeof x === "number"`: that was true
 * of the legacy payload and is false of this one.
 *
 * `year` and `month` only. There is no range form — an "all time" operating
 * margin is a figure no service defines.
 */
export function fetchAdminPerformanceOverview(params = {}) {
  const sp = new URLSearchParams();
  // P2: the endpoint takes EITHER a single month OR an inclusive window, never
  // both — it answers 422 on a mix rather than guessing. Each key is forwarded
  // only when the caller set it, so the two shapes stay mutually exclusive here
  // by construction instead of by convention.
  for (const key of [
    "year",
    "month",
    "from_year",
    "from_month",
    "to_year",
    "to_month",
    "comparison_from_year",
    "comparison_from_month",
    "comparison_to_year",
    "comparison_to_month",
  ]) {
    if (params[key] != null) sp.set(key, String(params[key]));
  }
  const qs = sp.toString();
  const url = `${API_BASE_URL}/api/admin/dashboard/performance${qs ? `?${qs}` : ""}`;
  return fetch(url, { headers: getApiHeaders(), signal: params.signal })
    .then((res) => {
      if (!res.ok) throw new Error("Performance-Daten konnten nicht geladen werden.");
      return res.json();
    })
    .catch((e) => {
      throw normalizeFetchError(e, "Performance-Daten konnten nicht geladen werden.");
    });
}
